import React, { useState } from "react";
import { DeleteFilled } from "@ant-design/icons";
import { Button, Form, Input, Modal, Select, Table, message } from "antd";
import {
  useGetDriversQuery,
  useAddDriverMutation,
  useDeleteDriverMutation,
  useGetCarsQuery,
} from "../../../redux/Service/api";
import { ExclamationCircleFilled } from "@ant-design/icons";
import Heading from "../../../Components/Heading/Heading";
import ServerError from "../../../Components/utils/ServerError";

const { confirm } = Modal;
const { Option } = Select;

interface Driver {
  DriverID: number;
  DriverName: string;
  Address: string;
  Phone: string;
  CarId: number;
}

const Drivers: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const [form] = Form.useForm();

  const [addDriver, { isLoading }] = useAddDriverMutation();
  const [deleteDriver, { isLoading: delIsLoading }] = useDeleteDriverMutation();
  const {
    data: driversData,
    isError: isDriversError,
    isLoading: isDriversLoading,
  } = useGetDriversQuery(undefined);
  const {
    data: carsData,
    isError: isCarsError,
    isLoading: isCarsLoading,
  } = useGetCarsQuery(undefined);

  const drivers: Driver[] = driversData?.ResponseObject ?? [];
  const cars = carsData?.ResponseObject ?? [];

  const handleAddDriver = async (values: any) => {
    try {
      await addDriver(values);
      message.success("تمت إضافة السائق بنجاح");
      form.resetFields();
      setOpenModal(false);
    } catch (error) {
      console.error("خطأ أثناء إضافة السائق:", error);
      message.error("فشل في إضافة السائق. يرجى المحاولة مرة أخرى.");
    }
  };

  const handleDeleteDriver = (id: number) => {
    confirm({
      title: "هل أنت متأكد أنك تريد حذف هذا السائق؟",
      icon: <ExclamationCircleFilled />,
      content: "بالضغط على موافق سيتم حذف السائق بشكل دائم.",
      async onOk() {
        try {
          await deleteDriver(id);
          message.success("تم حذف السائق بنجاح");
        } catch (error) {
          console.error(error);
          message.error("فشل في حذف السائق.");
        }
      },
    });
  };

  const columns = [
    {
      title: "اسم السائق",
      dataIndex: "DriverName",
      key: "DriverName",
    },
    {
      title: "العنوان",
      dataIndex: "Address",
      key: "Address",
    },
    {
      title: "رقم الهاتف",
      dataIndex: "Phone",
      key: "Phone",
    },
    {
      title: "رقم السيارة",
      dataIndex: "CarId",
      key: "CarId",
      render: (carId: number) => {
        const car = cars.find((car: any) => car.CarID === carId);
        return car?.CarNumber || "غير معروف";
      },
    },
    {
      title: "الإجراءات",
      key: "actions",
      render: (_: any, driver: Driver) => (
        <div className="flex gap-2">
          <Button
            type="text"
            icon={<DeleteFilled />}
            danger
            onClick={() => handleDeleteDriver(driver.DriverID)}
          />
        </div>
      ),
    },
  ];

  return (
    <section className="p-4 section_padding mt-0 pt-0 relative">
      <Heading heading="السائقين" />

      {isDriversError && <ServerError />}

      <div className="text-center my-3">
        <Button
          className="w-[150px]"
          type="primary"
          onClick={() => setOpenModal(true)}
        >
          إضافة سائق
        </Button>
      </div>

      <div className="mx-auto">
        <Table
          columns={columns}
          dataSource={drivers}
          rowKey="DriverID"
          loading={isDriversLoading || delIsLoading}
          pagination={{ pageSize: 5 }}
        />
      </div>

      <Modal
        title="إضافة سائق"
        centered
        open={openModal}
        onCancel={() => {
          form.resetFields();
          setOpenModal(false);
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddDriver}
          autoComplete="off"
        >
          <Form.Item
            label="اسم السائق"
            name="DriverName"
            rules={[{ required: true, message: "يرجى إدخال اسم السائق" }]}
          >
            <Input size="large" placeholder="أدخل اسم السائق" />
          </Form.Item>

          <Form.Item
            label="العنوان"
            name="Address"
            rules={[{ required: true, message: "يرجى إدخال العنوان" }]}
          >
            <Input size="large" placeholder="أدخل العنوان" />
          </Form.Item>

          <Form.Item
            label="رقم الهاتف"
            name="Phone"
            rules={[
              { required: true, message: "يرجى إدخال رقم الهاتف" },
              { pattern: /^[0-9]+$/, message: "يجب أن يحتوي على أرقام فقط" },
            ]}
          >
            <Input size="large" placeholder="أدخل رقم الهاتف" />
          </Form.Item>

          <Form.Item
            label="رقم السيارة"
            name="CarId"
            rules={[{ required: true, message: "يرجى اختيار رقم السيارة" }]}
          >
            {isCarsError ? (
              <div className="text-red-500">
                فشل في تحميل السيارات. يرجى المحاولة لاحقًا.
              </div>
            ) : (
              <Select
                size="large"
                placeholder="اختر رقم السيارة"
                loading={isCarsLoading}
              >
                {cars.map((car: any) => (
                  <Option key={car.CarID} value={car.CarID}>
                    {car.CarNumber}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={isLoading} block>
              إضافة
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </section>
  );
};

export default Drivers;
